import { useState } from 'react';
import { Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { login, isAuthenticated } from '../auth';
import { Brand } from '../components/Layout';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  if (isAuthenticated()) {
    return <Navigate to="/admin/users" replace />;
  }

  function validate() {
    const newErrors = {};
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!password) {
      newErrors.password = 'Please enter your password.';
    }
    return newErrors;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }
    setFieldErrors({});
    setLoading(true);
    setError('');
    const success = await login(email, password);
    if (success) {
      const redirectTo = location.state?.from || '/admin/users';
      navigate(redirectTo, { replace: true });
    } else {
      setError('Invalid email or password. Please try again.');
      setLoading(false);
    }
  }

  function handleEmailChange(e) {
    setEmail(e.target.value);
    if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: '' }));
  }

  function handlePasswordChange(e) {
    setPassword(e.target.value);
    if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: '' }));
  }

  return (
    <main className="min-h-screen grid place-items-center relative overflow-hidden bg-gradient-to-b from-primary-dark via-primary to-primary-deep p-4 text-white">
      {/* 1. Animated Radial Cyber Grid Mesh */}
      <div className="absolute inset-0 opacity-40 bg-[radial-gradient(rgba(192,132,252,0.8)_1.5px,transparent_1.5px)] [background-size:28px_28px] animate-grid-pulse pointer-events-none" />

      {/* 2. Animated Laser Sweep Beam Line */}
      {/* <div className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-accent-bright to-transparent shadow-[0_0_20px_var(--color-accent-bright)] animate-laser pointer-events-none z-10" /> */}

      {/* 3. Floating Holographic Ambient Glow Bulbs */}
      <div className="absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-accent-bright/15 blur-3xl float-slow pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 w-[420px] h-[420px] rounded-full bg-cta/25 blur-3xl float-medium pointer-events-none" />
      <div className="absolute top-1/3 right-1/4 w-72 h-72 rounded-full bg-accent-bright/10 blur-3xl float-slow pointer-events-none" />

      {/* 4. Floating 3D Vector Shield Node Badges */}
      <div className="absolute top-12 left-12 hidden lg:flex items-center gap-3 p-3.5 rounded-2xl glass-dark border border-white/15 shadow-2xl float-slow pointer-events-none">
        <span className="w-10 h-10 rounded-xl bg-accent-bright/20 border border-accent-bright/40 text-accent-bright grid place-items-center font-bold text-base">
          🛡️
        </span>
        <div>
          <b className="block text-xs text-white font-bold">ISO 9001:2015</b>
          <span className="text-[10px] text-accent-bright font-bold">Encrypted Dispatch</span>
        </div>
      </div>

      <div className="absolute bottom-12 right-12 hidden lg:flex items-center gap-3 p-3.5 rounded-2xl glass-dark border border-white/15 shadow-2xl float-medium pointer-events-none">
        <span className="w-10 h-10 rounded-xl bg-cta/20 border border-cta/40 text-cta grid place-items-center font-bold text-base">
          ⚡
        </span>
        <div>
          <b className="block text-xs text-white font-bold">FoSTaC Standard</b>
          <span className="text-[10px] text-accent-bright font-bold">Admin Portal</span>
        </div>
      </div>

      {/* ─── LOGIN GLASS CARD FORM ─── */}
      <div className="relative z-20 w-full max-w-[440px] glass-dark rounded-[32px] p-8 md:p-10 shadow-2xl border-2 border-white/20 animate-rise">
        {/* Brand Header */}
        <div className="mb-8 pb-4 border-b border-white/15">
          <Brand light={true} />
        </div>

        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Welcome back</h1>
          <p className="text-xs text-white/70 mt-1 font-medium">Sign in to manage customers, history &amp; services.</p>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-6" noValidate>
          <label className="grid gap-1.5 text-[10px] font-extrabold text-accent-bright tracking-wider uppercase">
            <span>EMAIL <span className="text-red-400">*</span></span>
            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter admin email"
              className={`w-full px-4 py-3.5 rounded-xl border bg-white/10 text-xs md:text-sm text-white placeholder-white/50 outline-none transition-all font-medium ${fieldErrors.email ? 'border-red-400 ring-2 ring-red-500/20' : 'border-white/20 focus:border-accent focus:ring-4 focus:ring-accent/20'
                }`}
            />
            {fieldErrors.email && <span className="text-[11px] font-bold text-red-300 normal-case">{fieldErrors.email}</span>}
          </label>

          <div className="grid gap-1.5 text-[10px] font-extrabold text-accent-bright tracking-wider uppercase">
            <div className="flex justify-between items-center">
              <span>PASSWORD <span className="text-red-400">*</span></span>
              <Link to="/forgot-password" className="text-accent hover:text-white transition-colors capitalize text-[10px]">Forgot Password?</Link>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                placeholder="Enter your password"
                className={`w-full pl-4 pr-12 py-3.5 rounded-xl border bg-white/10 text-xs md:text-sm text-white placeholder-white/50 outline-none transition-all font-medium ${fieldErrors.password ? 'border-red-400 ring-2 ring-red-500/20' : 'border-white/20 focus:border-accent focus:ring-4 focus:ring-accent/20'
                  }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors p-1"
                aria-label="Toggle password visibility"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {fieldErrors.password && <span className="text-[11px] font-bold text-red-300 normal-case">{fieldErrors.password}</span>}
          </div>

          {error && (
            <div className="bg-error/20 text-white border border-error/40 p-3.5 rounded-xl text-xs font-semibold backdrop-blur-sm">
              {error}
            </div>
          )}

          <button disabled={loading} className="btn-flash mt-2 w-full justify-center bg-cta hover:bg-cta-hover text-white font-extrabold text-sm py-4 px-6 rounded-2xl flex items-center gap-2 glow-cta transition-all border-0 shadow-lg cursor-pointer disabled:opacity-70 disabled:cursor-wait" type="submit">
            {loading ? 'Signing In...' : 'Sign In to Dashboard'} <span>→</span>
          </button>

          <div className="mt-4 text-center">
            <Link to="/" className="text-xs font-semibold text-white/60 hover:text-white no-underline transition-colors flex items-center justify-center gap-1.5">
              ← Return to Website
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
}
